import { config } from '@/config';

/**
 * Thin fetch wrapper. Response shapes are not re-declared here — they are imported
 * from `@timelog/pm-shared`, the same module the API builds its route schemas from,
 * so a contract change is a compile error on both sides.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

export type ApiClient = {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body: unknown) => Promise<T>;
  put: <T>(path: string, body: unknown) => Promise<T>;
  patch: <T>(path: string, body: unknown) => Promise<T>;
  delete: (path: string) => Promise<void>;
};

type GetAccessToken = (opts?: { forceRenew?: boolean }) => Promise<string | undefined>;

export type ApiClientOptions = {
  /**
   * Called when the API rejects the token even after a forced refresh — the
   * session is dead. Given the token that was rejected (undefined if the
   * request carried none), so the session can ignore a 401 about a token it
   * no longer holds: the sidebar's `/me`, sent before sign-in, used to answer
   * AFTER the user signed in and wipe the token they had just been given.
   */
  onUnauthorized?: (rejectedToken: string | undefined) => void;
};

const send = async (
  method: string,
  path: string,
  token: string | undefined,
  body?: unknown,
): Promise<Response> =>
  fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

const request = async <T>(
  getAccessToken: GetAccessToken,
  options: ApiClientOptions,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> => {
  const token = await getAccessToken();

  let response = await send(method, path, token, body);

  if (response.status === 401) {
    // The server rejected the token regardless of what we believe about its
    // expiry. Force one refresh and replay once; a second 401 (or nothing to
    // refresh with) means the session is dead — drop it to the sign-in gate.
    const fresh = await getAccessToken({ forceRenew: true });
    let rejected = token;

    if (fresh !== undefined && fresh !== token) {
      response = await send(method, path, fresh, body);
      rejected = fresh;
    }

    if (response.status === 401) {
      options.onUnauthorized?.(rejected);
    }
  }

  if (!response.ok) {
    // The API returns { statusCode, error, message } for every handled failure.
    const message = await response
      .json()
      .then((payload: { message?: string }) => payload.message)
      .catch(() => undefined);

    throw new ApiError(response.status, message ?? `Request failed with ${response.status}`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
};

export const createApiClient = (
  getAccessToken: GetAccessToken,
  options: ApiClientOptions = {},
): ApiClient => ({
  get: (path) => request(getAccessToken, options, 'GET', path),
  post: (path, body) => request(getAccessToken, options, 'POST', path, body),
  put: (path, body) => request(getAccessToken, options, 'PUT', path, body),
  patch: (path, body) => request(getAccessToken, options, 'PATCH', path, body),
  delete: async (path) => {
    await request(getAccessToken, options, 'DELETE', path);
  },
});
